// ============================================================================
// EduPath Sprint 5 - Sync Status Bar
// ============================================================================
// Persistent bar at top/bottom of screen showing sync status.
// Appears when offline, syncing, or when items need attention.
// Minimizes to a small indicator when everything is healthy.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useOfflineSync } from '@/hooks/useOfflineSync';

// ─── Utility: time ago formatting ────────────────────────────────────────────

function timeAgo(isoString: string | null): string {
  if (!isoString) return 'never';
  const diff = Date.now() - new Date(isoString).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

// ─── Icons (inline SVG to avoid dependencies) ───────────────────────────────

const Icons = {
  Cloud: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
    </svg>
  ),
  CloudOff: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M18 10h-1.26A8 8 0 0 0 4 14.46M6 19a5 5 0 0 0 12 0" />
    </svg>
  ),
  Sync: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ep-spin">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  Alert: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  X: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  ChevronDown: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  bar: {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: '13px',
    transition: 'all 0.3s ease',
  },
  barInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    gap: '12px',
  },
  leftGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  rightGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    borderRadius: '10px',
    padding: '0 6px',
    fontSize: '11px',
    fontWeight: 600 as const,
  },
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '4px',
    border: 'none',
    fontSize: '12px',
    fontWeight: 500 as const,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  btnSmall: {
    padding: '2px 6px',
    fontSize: '11px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    opacity: 0.7,
  },
  expandedPanel: {
    borderTop: '1px solid rgba(255,255,255,0.15)',
    padding: '12px 16px',
    maxHeight: '200px',
    overflowY: 'auto' as const,
  },
  queueItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 0',
    fontSize: '12px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
};

// Color themes for different states
const themes = {
  offline: {
    bg: '#1a1a2e',
    color: '#e0e0e0',
    accent: '#ff6b6b',
    badgeBg: 'rgba(255,107,107,0.2)',
    badgeColor: '#ff6b6b',
    btnBg: 'rgba(255,255,255,0.15)',
    btnColor: '#fff',
  },
  syncing: {
    bg: '#0a2647',
    color: '#e0e0e0',
    accent: '#5bc0eb',
    badgeBg: 'rgba(91,192,235,0.2)',
    badgeColor: '#5bc0eb',
    btnBg: 'rgba(91,192,235,0.25)',
    btnColor: '#5bc0eb',
  },
  issues: {
    bg: '#2d1b00',
    color: '#e0e0e0',
    accent: '#ffa62b',
    badgeBg: 'rgba(255,166,43,0.2)',
    badgeColor: '#ffa62b',
    btnBg: 'rgba(255,166,43,0.25)',
    btnColor: '#ffa62b',
  },
  healthy: {
    bg: '#0b2e1a',
    color: '#c0dcc8',
    accent: '#3ddc84',
    badgeBg: 'rgba(61,220,132,0.2)',
    badgeColor: '#3ddc84',
    btnBg: 'rgba(61,220,132,0.25)',
    btnColor: '#3ddc84',
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export const SyncStatusBar: React.FC = () => {
  const {
    syncStatus,
    queueItems,
    hasIssues,
    isSyncing,
    isOffline,
    pendingTotal,
    actionableItems,
    syncNow,
    retryItem,
    discardItem,
    openConflictModal,
  } = useOfflineSync();

  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when new issues appear
  useEffect(() => {
    if (hasIssues || isOffline || pendingTotal > 0) {
      setDismissed(false);
    }
  }, [hasIssues, isOffline, pendingTotal]);

  // Don't render if nothing to show and dismissed
  const shouldShow =
    !dismissed &&
    (isOffline || isSyncing || pendingTotal > 0 || hasIssues);

  if (!shouldShow) return null;

  // Determine theme
  const theme = isOffline
    ? themes.offline
    : hasIssues
      ? themes.issues
      : isSyncing
        ? themes.syncing
        : themes.healthy;

  // Status text
  const statusText = isOffline
    ? 'You are offline'
    : isSyncing
      ? 'Syncing assessments...'
      : hasIssues
        ? `${actionableItems.length} submission${actionableItems.length !== 1 ? 's' : ''} need attention`
        : pendingTotal > 0
          ? `${pendingTotal} pending`
          : 'All synced';

  // Status icon
  const StatusIcon = isOffline
    ? Icons.CloudOff
    : isSyncing
      ? Icons.Sync
      : hasIssues
        ? Icons.Alert
        : Icons.Check;

  // Non-synced items for the expanded panel
  const displayItems = queueItems.filter((i) => i.status !== 'SYNCED');

  return (
    <>
      {/* CSS for spin animation */}
      <style>{`
        @keyframes ep-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .ep-spin { animation: ep-spin 1.5s linear infinite; }
        .ep-sync-bar-btn:hover { opacity: 0.85 !important; }
      `}</style>

      <div style={{ ...styles.bar, background: theme.bg, color: theme.color }}>
        <div style={styles.barInner}>
          {/* Left: Status */}
          <div style={styles.leftGroup}>
            <span style={{ color: theme.accent }}>
              <StatusIcon />
            </span>
            <span>{statusText}</span>
            {pendingTotal > 0 && (
              <span
                style={{
                  ...styles.badge,
                  background: theme.badgeBg,
                  color: theme.badgeColor,
                }}
              >
                {pendingTotal}
              </span>
            )}
            {syncStatus.lastSyncAt && !isSyncing && (
              <span style={{ opacity: 0.5, fontSize: '11px' }}>
                · Last sync: {timeAgo(syncStatus.lastSyncAt)}
              </span>
            )}
          </div>

          {/* Right: Actions */}
          <div style={styles.rightGroup}>
            {displayItems.length > 0 && (
              <button
                className="ep-sync-bar-btn"
                style={{
                  ...styles.btn,
                  background: theme.btnBg,
                  color: theme.btnColor,
                }}
                onClick={() => setExpanded(!expanded)}
              >
                Details
                <span
                  style={{
                    transform: expanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                    display: 'inline-flex',
                  }}
                >
                  <Icons.ChevronDown />
                </span>
              </button>
            )}
            {!isSyncing && !isOffline && pendingTotal > 0 && (
              <button
                className="ep-sync-bar-btn"
                style={{
                  ...styles.btn,
                  background: theme.btnBg,
                  color: theme.btnColor,
                }}
                onClick={syncNow}
              >
                Sync Now
              </button>
            )}
            <button
              style={{ ...styles.btnSmall, color: theme.color }}
              onClick={() => setDismissed(true)}
              aria-label="Dismiss"
            >
              <Icons.X />
            </button>
          </div>
        </div>

        {/* Expanded queue detail */}
        {expanded && displayItems.length > 0 && (
          <div style={styles.expandedPanel}>
            {displayItems.map((item) => (
              <div key={item.id} style={styles.queueItem}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <StatusDot status={item.status} />
                  <span>
                    {item.assessmentType} — {item.learnerId.slice(0, 8)}…
                  </span>
                  <span style={{ opacity: 0.5 }}>
                    {timeAgo(item.queuedAt)}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {(item.status === 'FAILED' || item.status === 'REJECTED') && (
                    <>
                      <button
                        className="ep-sync-bar-btn"
                        style={{
                          ...styles.btn,
                          fontSize: '11px',
                          padding: '2px 8px',
                          background: 'rgba(91,192,235,0.2)',
                          color: '#5bc0eb',
                        }}
                        onClick={() => retryItem(item.id)}
                      >
                        Retry
                      </button>
                      <button
                        className="ep-sync-bar-btn"
                        style={{
                          ...styles.btn,
                          fontSize: '11px',
                          padding: '2px 8px',
                          background: 'rgba(255,107,107,0.15)',
                          color: '#ff6b6b',
                        }}
                        onClick={() => discardItem(item.id)}
                      >
                        Discard
                      </button>
                    </>
                  )}
                  {item.status === 'CONFLICT' && (
                    <button
                      className="ep-sync-bar-btn"
                      style={{
                        ...styles.btn,
                        fontSize: '11px',
                        padding: '2px 8px',
                        background: 'rgba(255,166,43,0.2)',
                        color: '#ffa62b',
                      }}
                      onClick={() => openConflictModal(item)}
                    >
                      Resolve
                    </button>
                  )}
                  {item.status === 'SYNCING' && (
                    <span style={{ opacity: 0.5, fontSize: '11px' }}>
                      Syncing…
                    </span>
                  )}
                  {item.status === 'PENDING' && (
                    <span style={{ opacity: 0.5, fontSize: '11px' }}>
                      Waiting
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// ─── Status Dot Indicator ────────────────────────────────────────────────────

const statusColors: Record<string, string> = {
  PENDING: '#5bc0eb',
  SYNCING: '#5bc0eb',
  SYNCED: '#3ddc84',
  FAILED: '#ffa62b',
  REJECTED: '#ff6b6b',
  CONFLICT: '#ffa62b',
};

const StatusDot: React.FC<{ status: string }> = ({ status }) => (
  <span
    style={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: statusColors[status] || '#888',
      display: 'inline-block',
      flexShrink: 0,
    }}
  />
);

export default SyncStatusBar;
